import { useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { CreateSessionForm } from "../components/CreateSessionForm"
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  useLoad,
} from "../components/ui"
import { api } from "../lib/api"

type SessionStatus =
  | "creating"
  | "starting"
  | "running"
  | "idle"
  | "stopping"
  | "stopped"
  | "failed"

type WorkspaceSession = {
  id: string
  workspaceId: string
  name: string
  status: SessionStatus
  idleTimeoutMinutes: number
  // Names only — the API never returns the values.
  envKeys: string[]
  idleSince: string | null
  lastActiveAt: string | null
  createdAt: string
  updatedAt: string
}

type Workspace = {
  id: string
  name: string
  repoUrl: string | null
}

export function WorkspaceSessionsPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const [showForm, setShowForm] = useState(false)

  const workspace = useLoad(async () => {
    if (!workspaceId) throw new Error("No workspace ID")
    const response = await api.get<{ workspace: Workspace }>(`/workspaces/${workspaceId}`)
    return response.workspace
  }, [workspaceId])

  const sessions = useLoad(async () => {
    if (!workspaceId) throw new Error("No workspace ID")
    const response = await api.get<{ sessions: WorkspaceSession[] }>(
      `/workspace-sessions?workspaceId=${workspaceId}`,
    )
    return response.sessions
  }, [workspaceId])

  if (workspace.loading) {
    return <Spinner />
  }

  if (workspace.error || !workspace.data) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <ErrorNote error={workspace.error || "Workspace not found"} />
        <Link to="/workspaces">
          <Button variant="ghost">Back to projects</Button>
        </Link>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title={`${workspace.data.name} - Sessions`}
        subtitle={workspace.data.repoUrl ?? "No repository"}
        action={
          <div className="flex gap-2">
            <Link to="/workspaces">
              <Button variant="ghost">Back to projects</Button>
            </Link>
            <Button onClick={() => setShowForm(true)} disabled={showForm}>
              Create Session
            </Button>
          </div>
        }
      />

      {showForm && (
        <CreateSessionForm
          workspaces={[workspace.data]}
          fixedWorkspaceId={workspaceId!}
          onCreated={(id) => navigate(`/workspace-sessions/${id}`)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {sessions.loading ? (
        <Spinner />
      ) : sessions.data?.length ? (
        <div className="flex flex-col gap-2">
          {sessions.data.map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              onDelete={() => sessions.reload()}
              onRefresh={() => sessions.reload()}
            />
          ))}
        </div>
      ) : (
        !showForm && (
          <Empty
            title="No sessions yet"
            hint="Create a session to start coding in an isolated environment."
          />
        )
      )}
    </>
  )
}

function SessionRow({
  session,
  onDelete,
  onRefresh,
}: {
  session: WorkspaceSession
  onDelete: () => void
  onRefresh: () => void
}) {
  const [deleting, setDeleting] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const statusColors: Record<SessionStatus, string> = {
    creating: "text-warning",
    starting: "text-warning",
    running: "text-success",
    idle: "text-muted",
    stopping: "text-warning",
    stopped: "text-muted",
    failed: "text-danger",
  }

  const handleDelete = async () => {
    if (!confirm("Delete this session? All data will be lost.")) return
    setDeleting(true)
    try {
      await api.delete(`/workspace-sessions/${session.id}`)
      onDelete()
    } catch {
      setDeleting(false)
    }
  }

  const handleStart = async () => {
    setActionLoading(true)
    try {
      await api.post(`/workspace-sessions/${session.id}/start`)
      onRefresh()
    } catch {
      setActionLoading(false)
    }
  }

  const handleStop = async () => {
    setActionLoading(true)
    try {
      await api.post(`/workspace-sessions/${session.id}/stop`)
      onRefresh()
    } catch {
      setActionLoading(false)
    }
  }

  const isRunning = session.status === "running"
  const isIdle = session.status === "idle"
  const isStopped = session.status === "stopped"
  const canConnect = isRunning || isIdle

  return (
    <Card className="flex items-center justify-between gap-4 p-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-foreground">{session.name}</p>
          <span className={`text-xs font-medium ${statusColors[session.status]}`}>
            {session.status}
          </span>
        </div>
        <div className="mt-1 flex gap-4 text-xs text-faint">
          <span>Idle timeout: {session.idleTimeoutMinutes}m</span>
          {session.lastActiveAt && (
            <span>Last active: {new Date(session.lastActiveAt).toLocaleString()}</span>
          )}
          {/* Names only. Showing that a session carries variables is useful;
              showing what is in them would defeat storing them encrypted. */}
          {session.envKeys?.length > 0 && (
            <span className="truncate" title={session.envKeys.join(", ")}>
              Env: {session.envKeys.join(", ")}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {canConnect && (
          <Link to={`/workspace-sessions/${session.id}`}>
            <Button>Connect</Button>
          </Link>
        )}

        {isStopped && (
          <Button variant="ghost" onClick={handleStart} disabled={actionLoading}>
            Start
          </Button>
        )}

        {isRunning && (
          <Button variant="ghost" onClick={handleStop} disabled={actionLoading}>
            Stop
          </Button>
        )}

        <Button variant="danger" onClick={handleDelete} disabled={deleting}>
          Delete
        </Button>
      </div>
    </Card>
  )
}
